// PoImportDocumentCard — one row of the import review list, expanding to the
// full per-document editor. One card per staged document; nothing here writes
// anything, it only shapes the draft the page will send to confirm().

import React from 'react';
import { Link } from 'react-router-dom';
import {
  formatCents,
  PO_LINE_TYPE_LABELS,
  type PoLineType,
} from './poApi';
import {
  IMPORT_STATUSES,
  IMPORT_STATUS_LABELS,
  averageConfidence,
  confidenceTier,
  CONFIDENCE_DOT_COLORS,
  centsToDollarsInput,
  dollarsToCents,
  isAiSource,
  reviewKey,
  PRODUCT_LABEL,
  type ImportLine,
  type ReviewTarget,
} from './poImportApi';
import { slotLabel } from '../components/review/grounding';
import { laneLabel as labelClass } from '../components/review/fieldStyles';
import type { ColumnDef, Table } from '@tanstack/react-table';
import { Alert } from '../components/ui/Alert';
import { Appear } from '../components/ui/Appear';
import { Checkbox } from '../components/ui/Checkbox';
import { EmptyState } from '../components/ui/EmptyState';
import { Panel } from '../components/ui/Panel';
import { DataTable } from '../components/table/DataTable';
import TableCheckbox from '../components/table/TableCheckbox';
import { Field, fieldClass } from '../components/ui/Field';
import { Select } from '../components/ui/Select';
import { Tag } from '../components/ui/Tag';
import { CATEGORY } from '../components/ui/categoryColors';
import { Tooltip } from '../components/ui/Tooltip';
import { PO_LINE_TYPE_TAG } from './poTones';
import EntityCombobox, { type ComboboxOption } from '../components/EntityCombobox';
import { ReviewNote, type ReviewSlot } from '../components/review/ReviewNote';
import DocumentPane from '../components/review/DocumentPane';
import {
  resolveReviews,
  reviewEntriesByKey,
  subtotalFromLines,
  previewSubjectForGroup,
  vendorCreateFromProposal,
  type DocDraft,
  type VendorEffective,
  type VendorOption,
} from './poImportDraft';
import type { VendorPreviewSubject } from './VendorPreviewModal';

/**
 * Every vendor as a picker row, in the order the endpoint sent them.
 *
 * `Retired` is where an is_disabled vendor's marker sits now that the name has a
 * line of its own — the same word the PO list uses, and typing it lists them
 * (sublabels are searchable). This lane asks for the disabled ones on purpose:
 * a four-year-old document's vendor may well have been retired since.
 */
function vendorRows(vendorList: VendorOption[], exceptId?: string | null): ComboboxOption[] {
  return vendorList
    .filter((v) => v.id !== exceptId)
    .map((v) => ({ id: v.id, label: v.name, sublabel: v.is_disabled ? 'Retired' : null }));
}

const VENDOR_DOT: Record<'green' | 'amber' | 'red' | 'neutral', string> = {
  green: 'bg-green-400',
  amber: 'bg-amber-400',
  red: 'bg-red-400',
  neutral: 'bg-outline',
};

// A vendor group is only computed over CHECKED documents (poImportDraft.ts —
// only what will actually be confirmed needs a decision), so an unchecked
// document always resolves to 'needs-name' whether or not it really has a
// vendor. Coloring that red would read as "this needs urgent attention" on a
// row the reviewer deliberately excluded — neutral is the honest answer for
// anything not going into this confirm.
function vendorTone(effectiveVendor: VendorEffective, included: boolean): 'green' | 'amber' | 'red' | 'neutral' {
  if (!included) return 'neutral';
  if (effectiveVendor.kind === 'matched' || effectiveVendor.kind === 'manual-existing') return 'green';
  if (effectiveVendor.kind === 'group') return effectiveVendor.resolved ? 'green' : 'amber';
  return 'red';
}

// Human labels for an enrichment's raw column names (vendors-service.js
// ENRICHABLE_VENDOR_FIELDS), collapsed so the five address columns read as
// one word — the reviewer never decides per-column, only "does the address
// get filled in", so five badges for one fact would be noise.
const ENRICHMENT_FIELD_LABELS: Record<string, string> = {
  contact_name: 'contact name',
  contact_email: 'contact email',
  contact_phone: 'contact phone',
  address_line1: 'address',
  address_line2: 'address',
  city: 'address',
  region: 'address',
  postal_code: 'address',
  country: 'address',
  payment_terms: 'payment terms',
};

/** "Will add from this PO: contact email, address" — one compact line, not a
 * per-field table, naming what an enrichment proposal will fill in without
 * repeating "address" five times for its five underlying columns. */
function enrichmentFieldSummary(fields: Record<string, string>): string {
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const key of Object.keys(fields)) {
    const label = ENRICHMENT_FIELD_LABELS[key] || key.replace(/_/g, ' ');
    if (!seen.has(label)) {
      seen.add(label);
      labels.push(label);
    }
  }
  return labels.join(', ');
}

function vendorSummaryLabel(effectiveVendor: VendorEffective, vendorList: VendorOption[], included: boolean): string {
  if (effectiveVendor.kind === 'matched' || effectiveVendor.kind === 'manual-existing') {
    return vendorList.find((v) => v.id === effectiveVendor.vendorId)?.name || effectiveVendor.label || 'matched vendor';
  }
  if (effectiveVendor.kind === 'group') {
    return effectiveVendor.resolved
      ? effectiveVendor.resolution.name
      : `${effectiveVendor.resolution.name} (needs a type)`;
  }
  return included ? 'needs a vendor' : 'not checked in';
}

// ---------------------------------------------------------------------------
// The not-sure treatment
// ---------------------------------------------------------------------------
//
// The server stages a list of places it was not sure of (po-import-service.js)
// and refuses the batch until each one has been answered. A ReviewSlot is one
// of those places as the card sees it: whether it is still open, the ring to
// put around its input, and the two ways to close it — type in the field, or
// say it looks right. Everything else on this card is unchanged for the ~190
// spreadsheets, which never carry a single entry.

// ReviewSlot / slotLabel / ReviewNote live in the review kit
// (components/review/, imported above) now that five lanes raise this gate. Same
// code, same look — the gate is one server list, so it gets one amber.

/**
 * A money field a person can actually type in.
 *
 * The naive controlled form — `value={centsToDollarsInput(cents)}` with an
 * onChange that parses straight back — re-formats on EVERY keystroke: typing "1"
 * snaps to "0.01", the caret jumps to the end, and deleting the decimal point is
 * impossible because it grows straight back. So the TEXT is local state and is
 * never rewritten while the field has focus; the cents are pushed up as fast as
 * they parse, so the draft stays live, and the text is normalized on blur.
 *
 * The effect re-syncs the text when the value changes from OUTSIDE (excluding a
 * line recomputes the subtotal), and only while the field is not being edited —
 * otherwise the recompute would overwrite whatever is half-typed.
 */
const MoneyInput: React.FC<{
  cents: number;
  onChangeCents: (cents: number) => void;
  className?: string;
  warn?: boolean;
  ariaLabel?: string;
}> = ({ cents, onChangeCents, className, warn, ariaLabel }) => {
  const [text, setText] = React.useState(() => centsToDollarsInput(cents));
  const editing = React.useRef(false);

  React.useEffect(() => {
    if (!editing.current) setText(centsToDollarsInput(cents));
  }, [cents]);

  return (
    <Field
      inputMode="decimal"
      warn={warn}
      className={className}
      aria-label={ariaLabel}
      value={text}
      onFocus={() => {
        editing.current = true;
      }}
      onChange={(e) => {
        setText(e.target.value);
        const parsed = dollarsToCents(e.target.value);
        if (parsed !== null) onChangeCents(parsed);
      }}
      onBlur={() => {
        editing.current = false;
        const parsed = dollarsToCents(text);
        setText(centsToDollarsInput(parsed === null ? cents : parsed));
      }}
    />
  );
};

type AttachLine = NonNullable<DocDraft['original']['attach_offer']>['lines'][number];

/**
 * Everything a cell needs that changes while somebody is reviewing travels by `meta`, not by
 * a closure in a column. NEVER wrap a cell renderer below in React.memo: tanstack memoizes the
 * cell context, so a memoized cell paints the previous answer.
 */
interface LineMeta {
  onPatchLine: (index: number, patch: Partial<ImportLine>) => void;
  lineSlots: Map<number, ReviewSlot>;
}
const lineMetaOf = (table: Table<ImportLine>): LineMeta => table.options.meta as LineMeta;

const LINE_COLUMNS: ColumnDef<ImportLine, any>[] = [
  {
    id: 'include',
    header: 'Include',
    cell: ({ row, table }) => {
      const line = row.original;
      const { onPatchLine, lineSlots } = lineMetaOf(table);
      return (
        <TableCheckbox
          aria-label={`Include line ${line.line_number}`}
          checked={line.included}
          onChange={(e) => {
            onPatchLine(row.index, { included: e.target.checked });
            lineSlots.get(line.line_number)?.markEdited();
          }}
        />
      );
    },
  },
  {
    id: 'description',
    header: 'Description',
    meta: { cellClass: 'max-w-xs' },
    // The description truncates; the review note must NOT (whitespace-nowrap would clip the
    // reader's own words to a few characters), so it sits outside the truncating div.
    cell: ({ row, table }) => {
      const line = row.original;
      const lineSlot = lineMetaOf(table).lineSlots.get(line.line_number);
      return (
        <>
          <Tooltip label={line.description || ''} truncated>
            <div className="truncate">
              {line.description || '—'}
              {line.from_totals_block && (
                <span className="ml-1.5 text-[10px] text-on-surface-variant">(from totals block)</span>
              )}
              {line.sku_guess && (
                <span className="ml-1.5 font-mono text-[10px] text-primary">{line.sku_guess}</span>
              )}
            </div>
          </Tooltip>
          {lineSlot && <ReviewNote slot={lineSlot} />}
        </>
      );
    },
  },
  {
    id: 'quantity',
    header: 'Qty',
    meta: { align: 'right', cellClass: 'tabular-nums' },
    cell: ({ row }) => row.original.quantity,
  },
  {
    id: 'unit_cost',
    header: 'Unit cost',
    meta: { align: 'right', cellClass: 'tabular-nums' },
    cell: ({ row }) => {
      const line = row.original;
      return line.unit_price_exact ? (
        formatCents(line.unit_cost_cents)
      ) : (
        <Tooltip label="Sub-cent price, rounded for storage">
          <span>
            ${line.unit_price_text} → {formatCents(line.unit_cost_cents)}
          </span>
        </Tooltip>
      );
    },
  },
  {
    id: 'line_type',
    header: 'Type',
    cell: ({ row, table }) => {
      const line = row.original;
      const { onPatchLine, lineSlots } = lineMetaOf(table);
      return (
        <Select
          label={`Line type, line ${line.line_number}`}
          size="sm"
          width="fit"
          value={line.line_type}
          onChange={(key) => {
            onPatchLine(row.index, { line_type: key as PoLineType });
            lineSlots.get(line.line_number)?.markEdited();
          }}
          options={(['stock', 'service', 'charge'] as PoLineType[]).map((t) => ({ key: t, label: PO_LINE_TYPE_LABELS[t] }))}
          renderValue={(o) => (o ? <Tag size="sm" {...PO_LINE_TYPE_TAG[o.key as PoLineType]}>{o.label}</Tag> : null)}
        />
      );
    },
  },
];

interface AttachMeta {
  attachSelected: Record<string, boolean>;
  onPatch: (patch: Partial<DocDraft>) => void;
}

const ATTACH_COLUMNS: ColumnDef<AttachLine, any>[] = [
  {
    id: 'attach',
    header: 'Attach',
    cell: ({ row, table }) => {
      const line = row.original;
      const { attachSelected, onPatch } = table.options.meta as AttachMeta;
      return (
        <TableCheckbox
          aria-label={`Attach ${line.sku || line.description || 'line'}`}
          checked={!!attachSelected[line.id]}
          onChange={(e) => onPatch({ attachSelected: { ...attachSelected, [line.id]: e.target.checked } })}
        />
      );
    },
  },
  {
    id: 'item',
    header: 'Description',
    // NOT ItemName, and not itemLabel. `description` on an attach-offer line is the PO LINE'S
    // NOTE, not the catalog description: po-import-service builds it as `description: l.notes`
    // with no catalog_item_names join. Preferring it over the SKU prints one buyer's note where
    // the identifier belongs. This cell gets a name the day that query joins one.
    cell: ({ row }) => row.original.sku || row.original.description || '—',
  },
  {
    id: 'expected',
    header: 'Expected',
    meta: { align: 'right', cellClass: 'tabular-nums' },
    cell: ({ row }) => row.original.quantity_expected,
  },
  {
    id: 'received',
    header: 'Received',
    meta: { align: 'right', cellClass: 'tabular-nums' },
    cell: ({ row }) => row.original.quantity_received,
  },
];

interface Props {
  draft: DocDraft;
  expanded: boolean;
  onToggleExpand: () => void;
  onPatch: (patch: Partial<DocDraft>) => void;
  onPatchLine: (index: number, patch: Partial<ImportLine>) => void;
  effectiveVendor: VendorEffective;
  blockingReasons: string[];
  /**
   * Which product this browser is. Only the foreign-PO panel needs it, and it
   * needs it to name US: "tracked in Firefleet" beats "tracked here" for somebody
   * who does not carry the two-instance architecture in their head.
   */
  localInstance: 'firefly' | 'swarm';
  vendorList: VendorOption[];
  onJumpToVendorPanel: () => void;
  onPreviewVendor: (subject: VendorPreviewSubject) => void;
  /**
   * The uploaded file this document was read from, straight out of the page's
   * own state — the batch never leaves the browser, so there is nothing to
   * fetch. Null when the parse response names a file the batch no longer holds,
   * which the pane says out loud instead of showing an empty frame.
   */
  documentFile?: File | null;
}

const PoImportDocumentCard: React.FC<Props> = ({
  draft,
  expanded,
  onToggleExpand,
  onPatch,
  onPatchLine,
  effectiveVendor,
  blockingReasons,
  localInstance,
  vendorList,
  onJumpToVendorPanel,
  onPreviewVendor,
  documentFile,
}) => {
  const ex = draft.original.extraction;
  if (!ex) return null;

  const tone = vendorTone(effectiveVendor, draft.include);
  const warnings = draft.original.warnings || [];
  const hasMismatch = warnings.some((w) => w.code === 'po_number_mismatch');
  const foreignWarning = warnings.find((w) => w.code === 'foreign_instance_po')?.message;
  const corrections = draft.original.workbook_overlay.applied_corrections;
  const flags = draft.original.workbook_overlay.flags;
  // The import lane only ever produces pdf_ai today (its upload filter refuses
  // images), but the question this asks is "did a model read it", not "was it a
  // PDF" — so it goes through the shared predicate.
  const isAiRead = isAiSource(draft.original.source);
  // The paper only shows where there IS paper: a spreadsheet reading is data,
  // not a document, so an xlsx gets no pane and no placeholder.
  //
  // `expanded` is repeated here on purpose, even though the pane renders inside
  // the expanded branch already. A batch runs to ~190 documents, DocumentPane
  // fetches on mount and holds an object URL until it unmounts, so one pane per
  // COLLAPSED row would be ~190 live blobs. The gate is stated where the pane is
  // decided, so moving the JSX cannot quietly drop it.
  const showDocumentPane = expanded && isAiRead;
  const conf = isAiRead ? averageConfidence(draft.original.confidence) : 1;
  const tier = confidenceTier(conf);
  const lineCount = draft.lines.length;
  // A document that is unchecked entirely is never "blocked" in any way that
  // matters right now — it is not going to confirm() at all, so a reason list
  // computed against an empty vendor-group pool (see poImportDraft.ts) would
  // just be noise. Only a CHECKED document's blocking reasons are real.
  const blocked = draft.include && blockingReasons.length > 0;
  // The subtotal follows the included lines automatically (poImportDraft.ts
  // patchLineInDraft), so these agree until somebody types over it. When they
  // disagree the reviewer is looking at a purchase order whose header will not
  // match its own lines, and the server will annotate it as such — so say so
  // here, where it can still be fixed with one click.
  const computedSubtotal = subtotalFromLines(draft.lines);
  const subtotalDiffers = computedSubtotal !== draft.subtotalCents;
  let subtotalHint = 'computed from included lines';
  if (subtotalDiffers) subtotalHint = 'edited';
  else if (ex.subtotal_source === 'computed') subtotalHint = 'from the lines: this document printed no usable subtotal';

  // ── the not-sure gate ────────────────────────────────────────────────────
  // Deliberately plain function calls rather than memos: this component has an
  // early return above it, so a hook here would be a conditional hook, and the
  // list is a handful of entries on the rare AI-read document.
  // Not the kit's groupSlots: this lane needs a per-call FACTORY that also
  // records which keys it anchored, so the panel below can show the rest.
  const reviewGroups = reviewEntriesByKey(draft);
  const anchored = new Set<string>();
  const slotFor = (target: ReviewTarget, field: string | null, lineNumber: number | null): ReviewSlot => {
    const key = reviewKey({ target, field, line_number: lineNumber });
    anchored.add(key);
    const entries = reviewGroups.get(key) || [];
    const open = entries.length > 0 && !draft.reviews[key];
    return {
      key,
      entries,
      open,
      label: slotLabel(target, field, lineNumber),
      // A RING alone. The fields are filled now and carry no outline for an
      // amber one to override, so the border half of this pair painted nothing.
      ring: open ? ' ring-2 ring-amber-500/60' : '',
      markEdited: () => {
        if (entries.length > 0) onPatch({ reviews: resolveReviews(draft, [key], 'edited') });
      },
      looksRight: () => onPatch({ reviews: resolveReviews(draft, [key], 'confirmed_as_staged') }),
    };
  };

  const orderDateSlot = slotFor('field', 'order_date', null);
  const quoteSlot = slotFor('field', 'quote_number', null);
  const shipDateSlot = slotFor('field', 'ship_date', null);
  const shipViaSlot = slotFor('field', 'ship_via', null);
  const termsSlot = slotFor('field', 'terms', null);
  const subtotalSlot = slotFor('field', 'subtotal_cents', null);
  const shipToSlot = slotFor('field', 'ship_to', null);
  const linesSlot = slotFor('field', 'lines', null);
  const vendorSlots = [slotFor('field', 'vendor', null), slotFor('field', 'vendor.name', null)]
    .filter((s) => s.entries.length > 0);
  const lineSlots = new Map(draft.lines.map((l) => [l.line_number, slotFor('line', null, l.line_number)]));

  // Anything the layout above has no home for (a document-level warning, the
  // PO number itself, a field a future reason introduces) lands here. Without
  // this the entry would be invisible AND unclearable, which is a document that
  // can never be confirmed and no way to see why.
  const looseGroups = [...reviewGroups.entries()].filter(([key]) => !anchored.has(key));
  const unreconciledCount = [...reviewGroups.keys()].filter((k) => !draft.reviews[k]).length;

  return (
    <div className={draft.include ? '' : 'opacity-60'}>
      {/* Row */}
      <div className="flex items-center gap-3 px-4 py-2.5 transition-colors duration-150 ease-emphasized hover:bg-surface-container motion-reduce:transition-none">
        <Checkbox
          className="shrink-0"
          checked={draft.include}
          onChange={(e) => onPatch({ include: e.target.checked })}
          aria-label={`Include ${draft.poNumber} in this confirm`}
        />
        <button
          type="button"
          onClick={onToggleExpand}
          className="flex-1 min-w-0 flex items-center gap-3 flex-wrap text-left"
        >
          <span className="font-mono text-primary text-sm whitespace-nowrap">{draft.poNumber}</span>
          {hasMismatch && (
            <Tooltip label="The document printed a different number; the filename was used.">
              <Tag size="sm" tone="warn">filename won</Tag>
            </Tooltip>
          )}
          {isAiRead && (
            <Tooltip label={`AI-read confidence: ${Math.round(conf * 100)}%`}>
              <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${CONFIDENCE_DOT_COLORS[tier]}`} />
            </Tooltip>
          )}
          <span className="flex items-center gap-1.5 text-xs">
            <span className={`inline-block w-2 h-2 rounded-full ${VENDOR_DOT[tone]}`} />
            <span className="text-on-surface-variant">{vendorSummaryLabel(effectiveVendor, vendorList, draft.include)}</span>
          </span>
          <span className="text-xs text-on-surface-variant">{lineCount} line{lineCount === 1 ? '' : 's'}</span>
          <span className="text-xs text-on-surface-variant tabular-nums">{formatCents(draft.subtotalCents)}</span>
          <Appear show={warnings.length > 0}>
            <Tag size="sm" tone="warn">
              {warnings.length} warning{warnings.length === 1 ? '' : 's'}
            </Tag>
          </Appear>
          <Appear show={corrections.length > 0}>
            <Tag size="sm" tone="info">
              {corrections.length} correction{corrections.length === 1 ? '' : 's'}
            </Tag>
          </Appear>
          {draft.original.attach_offer && (
            <Tag size="sm" tone="accent">
              {draft.original.attach_offer.lines.length} to attach
            </Tag>
          )}
          {draft.original.duplicate && (
            <Tag size="sm">already exists</Tag>
          )}
          {draft.original.amendment && (
            <Tag size="sm" hex={CATEGORY.purple.ink.hex}>
              rev {draft.original.amendment.revision}
            </Tag>
          )}
          {draft.original.foreign_entity && (
            <Tooltip label={`Raised by ${PRODUCT_LABEL[draft.original.foreign_entity]}, not by us`}>
              <Tag size="sm">{PRODUCT_LABEL[draft.original.foreign_entity]} PO</Tag>
            </Tooltip>
          )}
          <Appear show={unreconciledCount > 0}>
            <Tag size="sm" tone="warn">
              {unreconciledCount} to reconcile
            </Tag>
          </Appear>
          <Appear show={!!blocked}>
            <Tag size="sm" tone="bad">needs attention</Tag>
          </Appear>
        </button>
        <span className="text-on-surface-variant text-xs shrink-0">{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div
          className={`px-4 pb-4 pt-1 bg-surface-container-low border-t border-outline-variant${
            showDocumentPane ? ' grid grid-cols-1 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] gap-4' : ''
          }`}
        >
          <div className="space-y-4 min-w-0">
            <Alert show={!!draft.autoExcludedReason} tone="info" variant="note" className="!flex">
              {draft.autoExcludedReason}
            </Alert>
            <Alert show={!!blocked} tone="error" variant="note" className="!flex">
              <span className="block space-y-0.5">
                {blockingReasons.map((r) => <span key={r} className="block">{r}</span>)}
              </span>
            </Alert>
            {/* Neutral livery, deliberately. Amber below means "the reader was not
                sure", and wearing it here would say a model doubted something. A hue
                of its own is worse: Swarm's brand accent is a muted blue, so a blue
                chip reading "Firefleet" on the Swarm build paints the foreign product
                in the local product's color. The red `needs attention` chip already
                carries the to-do; this carries the fact.
                Rendered even when the document is UNCHECKED. Hiding it there looked
                tidy and made a dead end: blockingReasons is computed over every draft,
                so an auto-excluded Swarm duplicate showed "Needs a yes" with the
                checkbox nowhere on the page. Every other blocker keeps its control
                reachable while unchecked. */}
            {draft.original.foreign_entity && (
              <Alert
                tone="info"
                title={`${PRODUCT_LABEL[draft.original.foreign_entity]} raised this order`}
                live={false}
              >
                <div className="space-y-2">
                {/* The server's own sentence, verbatim. It is the only side that
                    knows whether this number falls in the range the other product's
                    app issues from, which is the difference between "nothing there
                    holds it" and "go and check". Duplicating that rule here would be
                    a second copy of a fact that changes per year. */}
                <p className="text-xs text-on-surface-variant">
                  {foreignWarning
                    || `${draft.poNumber} was raised by ${PRODUCT_LABEL[draft.original.foreign_entity]}. `
                      + `Tracking it in ${PRODUCT_LABEL[localInstance]} is allowed, and it is how an order gets `
                      + `received where the goods land.`}
                </p>
                <Checkbox
                  className="text-xs"
                  checked={draft.foreignAck}
                  onChange={(e) => onPatch({ foreignAck: e.target.checked })}
                  aria-label={`Yes, track ${draft.poNumber} in ${PRODUCT_LABEL[localInstance]}`}
                  label={`Yes, track ${draft.poNumber} in ${PRODUCT_LABEL[localInstance]}.`}
                />
                </div>
              </Alert>
            )}
            <Alert show={looseGroups.length > 0} tone="warning" title="The reader was not sure" live={false}>
                <div className="mt-2 space-y-2">
                {looseGroups.map(([key, entries]) => (
                  <div key={key}>
                    <ReviewNote
                      slot={{
                        key,
                        entries,
                        open: !draft.reviews[key],
                        label: slotLabel(entries[0].target, entries[0].field, entries[0].line_number),
                        ring: '',
                        markEdited: () => {},
                        looksRight: () => onPatch({ reviews: resolveReviews(draft, [key], 'confirmed_as_staged') }),
                      }}
                    />
                  </div>
                ))}
                </div>
              </Alert>
            {draft.original.duplicate && (
              <Alert tone="info" variant="note" className="!flex">
                <span>
                This purchase order already exists in this database (status: {draft.original.duplicate.status}).{' '}
                <Link
                  to={`/procurement/po/${draft.original.duplicate.existing_po_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline"
                >
                  Open it
                </Link>
                {' '}to fix a field directly. Importing this document again will not change it.
                </span>
              </Alert>
            )}
            {draft.original.amendment && (
              <Alert tone="info" variant="note" className="!flex">
                <span>
                This is revision {draft.original.amendment.revision} of {draft.original.amendment.base_po_number}.
                Historical revisions share their base's purchase-order number here (there is no distinct "-R1" number
                the way an in-app amendment mints one), so this imports AS {draft.poNumber}. It does not create a
                second document. If the original, unrevised file for this number is elsewhere in this batch, leave it
                unchecked; the revision is the one that should land.
                </span>
              </Alert>
            )}

            {/* Header fields */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              <label className="block">
                <span className={labelClass}>Status</span>
                <Select
                  label="Status"
                  value={draft.status}
                  onChange={(key) => onPatch({ status: key as DocDraft['status'] })}
                  options={IMPORT_STATUSES.map((st) => ({ key: st, label: IMPORT_STATUS_LABELS[st] }))}
                />
              </label>
              <label className="block">
                <span className={labelClass}>
                  Order date <Appear show={!draft.orderDate} className="text-red-400">(required)</Appear>
                </span>
                <Field
                  type="date"
                  error={!draft.orderDate}
                  warn={!!orderDateSlot.ring}
                  value={draft.orderDate}
                  onChange={(e) => {
                    onPatch({ orderDate: e.target.value });
                    orderDateSlot.markEdited();
                  }}
                />
                <ReviewNote slot={orderDateSlot} />
              </label>
              <label className="block">
                <span className={labelClass}>Quote number</span>
                <Field
                  warn={!!quoteSlot.ring}
                  value={draft.quoteNumber}
                  onChange={(e) => {
                    onPatch({ quoteNumber: e.target.value });
                    quoteSlot.markEdited();
                  }}
                />
                <ReviewNote slot={quoteSlot} />
              </label>
              <label className="block">
                <span className={labelClass}>Ship date</span>
                <Field
                  type="date"
                  warn={!!shipDateSlot.ring}
                  value={draft.shipDate}
                  onChange={(e) => {
                    onPatch({ shipDate: e.target.value });
                    shipDateSlot.markEdited();
                  }}
                />
                <ReviewNote slot={shipDateSlot} />
              </label>
              <label className="block">
                <span className={labelClass}>Ship via</span>
                <Field
                  warn={!!shipViaSlot.ring}
                  value={draft.shipVia}
                  onChange={(e) => {
                    onPatch({ shipVia: e.target.value });
                    shipViaSlot.markEdited();
                  }}
                />
                <ReviewNote slot={shipViaSlot} />
              </label>
              <label className="block">
                <span className={labelClass}>Terms</span>
                <Field
                  warn={!!termsSlot.ring}
                  value={draft.terms}
                  onChange={(e) => {
                    onPatch({ terms: e.target.value });
                    termsSlot.markEdited();
                  }}
                />
                <ReviewNote slot={termsSlot} />
              </label>
              {/* Deliberately a div, not a label: the "Use that" button below sits
                  inside this cell, and a wrapping label would forward its click to
                  the input, focusing the field and suppressing the re-sync that
                  should show the new figure. */}
              <div className="block">
                <span className={labelClass}>
                  Subtotal
                  <span className="text-on-surface-variant"> ({subtotalHint})</span>
                </span>
                <MoneyInput
                  warn={!!subtotalSlot.ring}
                  className="tabular-nums"
                  ariaLabel={`Subtotal for ${draft.poNumber}`}
                  cents={draft.subtotalCents}
                  onChangeCents={(cents) => {
                    onPatch({ subtotalCents: cents });
                    subtotalSlot.markEdited();
                  }}
                />
                <ReviewNote slot={subtotalSlot} />
                <Appear as="div" show={!!subtotalDiffers} className="mt-0.5 text-[10px] text-amber-300">
                  <span>
                    Included lines add up to {formatCents(computedSubtotal)}.{' '}
                    <button
                      type="button"
                      className="underline hover:text-amber-200"
                      onClick={() => onPatch({ subtotalCents: computedSubtotal })}
                    >
                      Use that
                    </button>
                  </span>
                </Appear>
              </div>
              <label className="block">
                <span className={labelClass}>Reviewer note (optional)</span>
                <Field value={draft.notes} onChange={(e) => onPatch({ notes: e.target.value })} />
              </label>
            </div>

            {/* One slot for the whole ship-to block: the lane's confidence is
                reported for the block, not per line of the address. */}
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="block">
                  <span className={labelClass}>Ship to</span>
                  <Field
                    warn={!!shipToSlot.ring}
                    value={draft.shipToName}
                    onChange={(e) => {
                      onPatch({ shipToName: e.target.value });
                      shipToSlot.markEdited();
                    }}
                  />
                </label>
                <label className="block">
                  <span className={labelClass}>Attn</span>
                  <Field
                    warn={!!shipToSlot.ring}
                    value={draft.shipToAttn}
                    onChange={(e) => {
                      onPatch({ shipToAttn: e.target.value });
                      shipToSlot.markEdited();
                    }}
                  />
                </label>
                <label className="block">
                  <span className={labelClass}>Address</span>
                  <Field
                    warn={!!shipToSlot.ring}
                    value={draft.shipToAddress}
                    onChange={(e) => {
                      onPatch({ shipToAddress: e.target.value });
                      shipToSlot.markEdited();
                    }}
                  />
                </label>
              </div>
              <ReviewNote slot={shipToSlot} />
            </div>

            {/* Vendor */}
            <div>
              <VendorSection
                draft={draft}
                effectiveVendor={effectiveVendor}
                vendorList={vendorList}
                onPatch={onPatch}
                onJumpToVendorPanel={onJumpToVendorPanel}
                onPreviewVendor={onPreviewVendor}
                reviewRing={vendorSlots.some((s) => s.open) ? ' ring-2 ring-amber-500/60' : ''}
                // ONE patch, not one per slot: two markEdited() calls both derive
                // from this render's draft.reviews, so the second would drop the
                // first's answer on the floor.
                onVendorEdited={() => {
                  if (vendorSlots.length > 0) {
                    onPatch({ reviews: resolveReviews(draft, vendorSlots.map((s) => s.key), 'edited') });
                  }
                }}
              />
              {vendorSlots.map((slot) => (
                <ReviewNote key={slot.key} slot={slot} />
              ))}
            </div>

            {/* Workbook corrections + flags */}
            <Alert show={corrections.length > 0 || flags.length > 0} tone="info" title="Reconciliation workbook" live={false}>
                <div className="mt-1.5 space-y-1.5">
                {corrections.map((c, i) => (
                  <div key={i} className="text-xs text-on-surface-variant">
                    <span className="text-on-surface-variant">{c.field}:</span>{' '}
                    {c.document_value ? <span className="line-through text-on-surface-variant">{c.document_value}</span> : null}
                    {' → '}
                    <span className="text-indigo-300">{c.workbook_value}</span>
                    {c.reason && <span className="text-on-surface-variant"> ({c.reason})</span>}
                  </div>
                ))}
                {flags.map((f, i) => (
                  <div key={i} className="text-xs text-amber-300">{f.message}</div>
                ))}
                </div>
              </Alert>

            {/* Lines */}
            <div>
              <div className={`rounded-panel${linesSlot.ring}`}>
                <DataTable<ImportLine>
                  data={draft.lines}
                  columns={LINE_COLUMNS}
                  getRowId={(_, index) => String(index)}
                  on="panel"
                  density="compact"
                  meta={{ onPatchLine, lineSlots }}
                  rowProps={(line) => {
                    const lineSlot = lineSlots.get(line.line_number);
                    return {
                      className: [
                        line.included ? '' : 'opacity-50',
                        lineSlot && lineSlot.open ? 'bg-amber-500/[0.06]' : '',
                      ].filter(Boolean).join(' '),
                    };
                  }}
                  empty={<EmptyState variant="area">No lines were read from this document.</EmptyState>}
                />
              </div>
              <ReviewNote slot={linesSlot} />
            </div>

            {/* Attach offer */}
            {draft.original.attach_offer && (
              <Panel on="panel" padding="sm" label="Existing receiving lines to attach" className="ring-1 ring-primary/30 space-y-2">
                <p className="text-xs text-on-surface-variant">
                  {draft.original.attach_offer.lines.length} existing receiving line
                  {draft.original.attach_offer.lines.length === 1 ? '' : 's'} already reference{' '}
                  {draft.original.attach_offer.purchase_order_ref} with no purchase order behind them yet. Attaching
                  adopts them onto the document created here instead of creating duplicate lines. What has already
                  arrived stays exactly as recorded.
                </p>
                <DataTable<AttachLine>
                  data={draft.original.attach_offer.lines}
                  columns={ATTACH_COLUMNS}
                  getRowId={(line) => line.id}
                  on="panel"
                  density="compact"
                  meta={{ attachSelected: draft.attachSelected, onPatch }}
                  empty={null}
                />
              </Panel>
            )}

            {/* Manual supersedes (rare) */}
            <label className="block max-w-xs">
              <span className={labelClass}>Supersedes PO number (optional)</span>
              <Field
                placeholder="e.g. PO-FF-TEC26010"
                value={draft.supersedesPoNumber}
                onChange={(e) => onPatch({ supersedesPoNumber: e.target.value })}
              />
            </label>
          </div>

          {/* The paper, beside the reading, on the same split as the AP review page:
              fields one side, document the other, stacking below xl because this
              card's left column carries a five-column line table. Deliberately NOT
              sticky — the Documents section clips with overflow-hidden, which makes a
              sticky child silently never stick.
              A File IS a Blob, so this lane needs no proxy and no storage: the uploaded
              document is still in the page's own state, so the paper lives exactly as
              long as the batch does and dies with it. A null file means the parse
              response named a document this batch no longer holds — the pane's own
              could-not-be-fetched state carries that, rather than an empty frame.
              The mime is a constant because the upload filter admits only .xlsx and
              .pdf, and a spreadsheet never reaches here. */}
          {showDocumentPane && (
            <DocumentPane
              fetchBlob={
                documentFile
                  ? () => Promise.resolve(documentFile)
                  : () => Promise.reject(new Error(`${draft.original.source_filename} is no longer in this batch.`))
              }
              refreshKey={documentFile ? `${documentFile.name}:${documentFile.size}` : `missing:${draft.original.source_filename}`}
              mimeType="application/pdf"
              emptyCopy="The fields on this card are a proposal, so do not confirm a document you have not looked at."
              className="min-w-0"
            />
          )}
        </div>
      )}
    </div>
  );
};

const VendorSection: React.FC<{
  draft: DocDraft;
  effectiveVendor: VendorEffective;
  vendorList: VendorOption[];
  onPatch: (patch: Partial<DocDraft>) => void;
  onJumpToVendorPanel: () => void;
  onPreviewVendor: (subject: VendorPreviewSubject) => void;
  /** Amber outline while the reader's doubt about this vendor is unanswered. */
  reviewRing: string;
  /** Touching any vendor control counts as having looked at it. */
  onVendorEdited: () => void;
}> = ({ draft, effectiveVendor, vendorList, onPatch, onJumpToVendorPanel, onPreviewVendor, reviewRing, onVendorEdited }) => {
  const vr = draft.original.vendor_resolution;

  if (draft.existingVendorOverrideId) {
    const name = vendorList.find((v) => v.id === draft.existingVendorOverrideId)?.name;
    return (
      <div className="text-xs text-on-surface-variant flex items-center gap-2 flex-wrap">
        <span className={labelClass + ' inline'}>Vendor</span>
        <button
          type="button"
          className="text-on-surface hover:text-primary hover:underline"
          onClick={() => onPreviewVendor({ mode: 'existing', vendorId: draft.existingVendorOverrideId, vendorName: name })}
        >
          {name}
        </button>
        <button type="button" className="text-primary hover:underline" onClick={() => onPatch({ existingVendorOverrideId: '' })}>
          change
        </button>
      </div>
    );
  }

  if (vr?.matched_vendor_id) {
    // The match is the first row and wears the `match` chip; every other row is
    // an override. That chip carries what the old "(not the match)" suffix said,
    // in the positive and once, rather than on all ~200 of the other rows.
    const matchRows: ComboboxOption[] = [
      {
        id: vr.matched_vendor_id,
        label: vr.matched_name || vendorList.find((v) => v.id === vr.matched_vendor_id)?.name || 'Matched vendor',
        chip: 'match',
      },
      ...vendorRows(vendorList, vr.matched_vendor_id),
    ];
    return (
      <div className="max-w-sm">
        <EntityCombobox
          label="Vendor (matched)"
          labelClassName={labelClass}
          inputClassName={fieldClass({ warn: reviewRing !== '' })}
          value={vr.matched_vendor_id}
          options={matchRows}
          onSelect={(o) => {
            const picked = o ? String(o.id) : '';
            // Taking the match again changes nothing: the draft already pins no
            // override, and the select it replaced fired no event for re-picking
            // its own value either. It must stay a no-op, or opening the list and
            // shutting it would clear the reader's vendor flag as an EDIT.
            // "Looks right" is the affirmation, and it records the honest reason.
            if (!picked || picked === vr.matched_vendor_id) return;
            onPatch({ existingVendorOverrideId: picked });
            onVendorEdited();
          }}
          emptyMessage="No vendor matches that."
        />
        <button
          type="button"
          className="mt-1 block text-[11px] text-primary hover:underline"
          onClick={() =>
            onPreviewVendor({
              mode: 'existing',
              vendorId: vr.matched_vendor_id as string,
              vendorName: vr.matched_name,
              enrichment: vr.enrichment?.fields ?? null,
            })
          }
        >
          Preview vendor
        </button>
        {vr.enrichment && (
          <p className="mt-1 text-[11px] text-on-surface-variant">
            Will add from this PO: {enrichmentFieldSummary(vr.enrichment.fields)}.
          </p>
        )}
      </div>
    );
  }

  if (effectiveVendor.kind === 'group') {
    return (
      <div className="text-xs text-on-surface-variant flex items-center gap-2 flex-wrap">
        <span className={labelClass + ' inline'}>Vendor</span>
        <button
          type="button"
          className={effectiveVendor.resolved ? 'text-green-300 hover:underline' : 'text-amber-300 hover:underline'}
          onClick={() => onPreviewVendor(previewSubjectForGroup(effectiveVendor.group, effectiveVendor.resolution, vendorList))}
        >
          {effectiveVendor.resolved
            ? effectiveVendor.resolution.name
            : `${effectiveVendor.resolution.name} (needs a type)`}
        </button>
        <button type="button" onClick={onJumpToVendorPanel} className="text-primary hover:underline">
          resolve in vendor triage ↓
        </button>
      </div>
    );
  }

  // A document that names a vendor but is currently UNCHECKED never has a
  // group to belong to (poImportDraft.ts only pools checked documents), so
  // effectiveVendor falls to 'needs-name' even though the document clearly
  // named someone. Read the real name straight off the proposal rather than
  // showing the "no vendor at all" input, which would be misleading.
  if (vr?.create_proposal) {
    const proposal = vr.create_proposal;
    return (
      <p className="text-xs text-on-surface-variant">
        Names{' '}
        <button
          type="button"
          className="text-on-surface hover:text-primary hover:underline"
          onClick={() => onPreviewVendor({ mode: 'proposal', proposal: vendorCreateFromProposal(proposal) })}
        >
          {proposal.name}
        </button>
        . Check this document in to resolve it in the vendor triage panel.
      </p>
    );
  }

  // Nameless document — the reviewer types one, or picks an existing vendor directly.
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
      <label className="block">
        <span className={labelClass}>Vendor name (none was read from this document)</span>
        <Field
          warn={reviewRing !== ''}
          value={draft.manualVendorName}
          onChange={(e) => {
            onPatch({ manualVendorName: e.target.value });
            onVendorEdited();
          }}
          placeholder="Type the vendor's name"
        />
      </label>
      {/* No action row here: the name typed on the left IS the create, so this
          list's empty answer is "nobody yet" and stays a row, the way the shared
          picker's every other empty answer does. */}
      <div className="block">
        <EntityCombobox
          label="Or pick an existing vendor"
          labelClassName={labelClass}
          inputClassName={fieldClass({ warn: reviewRing !== '' })}
          value={draft.existingVendorOverrideId}
          options={[{ id: '', label: 'None' }, ...vendorRows(vendorList)]}
          onSelect={(o) => {
            onPatch({ existingVendorOverrideId: o ? String(o.id) : '' });
            onVendorEdited();
          }}
          emptyMessage="No vendor matches that."
        />
      </div>
    </div>
  );
};

export default PoImportDocumentCard;
